<template>
    <v-app-bar :color="user ? 'primary' : 'transparent'"
               app
               clipped-right
               dense
               fixed
               flat
               :height="topNavigationHeight">
        <!-- if user is logged in -->
        <v-container v-if="user" pa-0 class="d-flex fill-height flex-nowrap">
            <!-- logo -->
            <v-avatar v-if="showTopBar"
                      class="mr-sm-3"
                      :class="{ 'mr-2' : user && user.role === 'owner', 'mr-3' : user && user.role !== 'owner' }"
                      :min-width="logoWidth"
                      :width="logoWidth"
                      height="auto"
                      tile>
                <router-link :to="dashboardUrl" class="d-inline-flex">
                    <v-img class="align-self-start"
                           :min-width="logoWidth"
                           :width="logoWidth"
                           :aspect-ratio="4/3"
                           contain
                           :to="dashboardUrl"
                           src="../../assets/images/logo_small.svg"></v-img>
                </router-link>
            </v-avatar>

            <div class="pl-sm-5 pr-1 pr-sm-6 mr-1 mr-md-6 d-flex align-center justify-space-between fill-height v-toolbar__mid"
                 :class="{ 'pl-2' : user && user.role === 'owner', 'pl-4' : user && user.role !== 'owner' }">
                <router-link :to="profileUrl" style="text-decoration: none">
                    <div class="d-flex align-center" id="topbar-useravatar">
                        <user-avatar :user="user" :size="userImgSize"></user-avatar>
                        <p class="pl-2 mb-0 secondary--text v-toolbar__user">
                            {{ user.first_name }}<br>
                            {{ user.last_name }}<br>
                            <span class="text-uppercase"
                                style="font-size: 9px"
                                :class="{ 'font-weight-bold' : rank.id == 1}"
                                v-html="$options.filters.master(rank.name)">
                            </span>
                        </p>
                    </div>
                </router-link>

                <!-- spacer -->
                <v-spacer></v-spacer>

                <!-- approvals button -->
                <v-btn v-if="showTopBar && user.role === 'owner'"
                       icon
                       color="white"
                       :to="{ name: 'approvals' }">
                    <v-badge color="error"
                             :value="itemsToApprove"
                             :content="itemsToApprove"
                             overlap
                             bottom>
                        <v-icon>mdi-file-document-outline</v-icon>
                    </v-badge>
                </v-btn>

                <!-- cart button -->
                <v-btn v-if="showTopBar"
                       icon
                       color="white"
                       :to="cartUrl">
                    <v-badge color="error"
                             :value="cartAmount"
                             :content="cartAmount"
                             overlap
                             bottom>
                        <v-icon>mdi-cart-outline</v-icon>
                    </v-badge>
                </v-btn>
            </div>


            <!-- spacer -->
            <v-spacer></v-spacer>

            <!-- main menu item -->
            <v-btn v-if="showTopBar"
                   @click="toggleSidebar"
                   class="mr-n2"
                   color="white"
                   icon
                   id="topbar-sidebartoggle">
                <v-icon v-if="!appSidebar">mdi-cog</v-icon>
                <v-icon v-if="appSidebar">mdi-close</v-icon>
            </v-btn>
        </v-container>

        <!-- if user is not logged in-->
        <v-container v-else pa-0 class="d-flex">
            <!-- close login form -->
            <v-btn v-if="!showTwoFactor && showLoginForm && !showBackToLogin"
                   @click.native="closeLoginForm"
                   icon
                   class="ml-n2"
                   color="white">
                <v-icon>mdi-arrow-left-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="showTwoFactor && showLoginForm && !showBackToLogin"
                   @click.native="closeTwoFactorForm"
                   icon
                   class="ml-n2"
                   color="white">
                <v-icon>mdi-arrow-left-circle-outline</v-icon>
            </v-btn>

            <!-- back to login -->
            <v-btn v-if="showBackToLogin"
                   :to="loginUrl"
                   icon
                   class="ml-n2"
                   color="white">
                <v-icon>mdi-arrow-left-circle-outline</v-icon>
            </v-btn>
            <!-- spacer -->
            <v-spacer></v-spacer>
            <!-- language switch -->
            <language-switch v-if="showLanguageSwitch"
                             :show="showLanguageSwitch" />
            <!-- imprint -->
            <static-content-dialog v-if="showLanguageSwitch"
                                   slug="imprint">
                <v-btn icon
                       class="mr-n2"
                       color="white">
                    <v-icon>mdi-card-text-outline</v-icon>
                </v-btn>
            </static-content-dialog>

            <!-- data privacy -->
            <static-content-dialog v-if="showLanguageSwitch"
                                   slug="data_protection">
                <v-btn icon
                       class="mr-n2"
                       color="white">
                    <v-icon>mdi-shield-outline</v-icon>
                </v-btn>
            </static-content-dialog>
        </v-container>
    </v-app-bar>
</template>

<script>
    // 3rd
    import {
        mapActions,
        mapState,
    } from 'vuex';
    // components
    import LanguageSwitch from '../LanguageSwitch';
    import UserAvatar from '../UserAvatar';
    import StaticContentDialog from '../StaticContentDialog';
    // models
    import Cart from '../../models/Cart';
    import Contract from '../../models/Contract';
    import ParticipantApproval from '../../models/ParticipantApproval';
    import Achievement from '../../models/Achievement';
    // utils
    import {
        ROUTE_CART,
        ROUTE_DASHBOARD,
        ROUTE_LOGIN,
        ROUTE_PROFILE,
        ROUTE_WISHLIST,
    } from '../../utils/constants';

    export default {
        name: 'app-topbar',
        components: {
            LanguageSwitch,
            UserAvatar,
            StaticContentDialog,
        },
        props: {
            user: {},
        },
        computed: {
            ...mapState([
                'appSidebar',
                'showLoginForm',
                'showTwoFactor',
            ]),
            cartAmount() {
                return Cart.getSize();
            },
            itemsToApprove() {
                const ParticipantApprovals = ParticipantApproval.query().where((participantApproval => {
                    return participantApproval.approved === false && participantApproval.declined === false;
                })).get();

                const Contracts = Contract.query().where((contract => {
                    return contract.approved === false && contract.declined === false;
                })).get();

                return ParticipantApprovals.length + Contracts.length;
            },
            showLanguageSwitch() {
                return !!this.$route.meta.showLanguageSwitch;
            },
            topNavigationHeight() {
                return this.$vuetify.breakpoint.smAndUp ? '100px' : '70px';
            },
            showBackToLogin() {
                return !!this.$route.meta.showBackToLogin;
            },
            showTopBar() {
                return !!this.$route.meta.showTopBar;
            },
            loginUrl() {
                return { name: ROUTE_LOGIN };
            },
            cartUrl() {
                return { name: ROUTE_CART };
            },
            wishlistUrl() {
                return { name: ROUTE_WISHLIST };
            },
            dashboardUrl() {
                return { name: ROUTE_DASHBOARD };
            },
            profileUrl() {
                return { name: ROUTE_PROFILE };
            },
            // breakpoint related
            logoWidth() {
                return this.$vuetify.breakpoint.smAndUp ? '86.5px' : '56px';
            },
            userImgSize() {
                return this.$vuetify.breakpoint.smAndUp ? 55 : 30;
            },
            rank() {
                return Achievement.getHighestRank();
            },
            rankName() {
                const rank = this.rank.name.split(' ');
                return rank.length > 1 ? `${rank[0]} <span class="font-weight-bold">${rank[1]}</span>` : rank[0];
            },
        },
        methods: {
            ...mapActions([
                'toggleSidebar',
                'closeLoginForm',
                'closeTwoFactorForm',
            ]),
        },
    };
</script>
